
import React, { useEffect, useRef, memo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Animated } from 'react-native';
import Svg, { Path } from 'react-native-svg';

interface AnimatedMessageContextMenuProps {
  visible: boolean;
  onShare: () => void;
  onBookmark: () => void;
  onClose: () => void;
  position: { x: number; y: number };
  isBookmarked?: boolean;
  isUserMessage?: boolean;
  messageWidth?: number;
  messageHeight?: number;
}

const AnimatedMessageContextMenu = memo(function AnimatedMessageContextMenu({
  visible,
  onShare,
  onBookmark,
  onClose,
  isBookmarked = false,
  isUserMessage = false,
}: AnimatedMessageContextMenuProps) {
  const containerScale = useRef(new Animated.Value(0)).current;
  const containerOpacity = useRef(new Animated.Value(0)).current;
  const overlayOpacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (visible) {
      // Show overlay first
      Animated.timing(overlayOpacity, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }).start();

      // Animate container with scale and opacity
      setTimeout(() => {
        Animated.parallel([
          Animated.timing(containerOpacity, {
            toValue: 1,
            duration: 150,
            useNativeDriver: true,
          }),
          Animated.sequence([
            Animated.spring(containerScale, {
              toValue: 1.05,
              useNativeDriver: true,
              tension: 150,
              friction: 8,
            }),
            Animated.spring(containerScale, {
              toValue: 1,
              useNativeDriver: true,
              tension: 180,
              friction: 10,
            }),
          ]),
        ]).start();
      }, 50);
    } else {
      // Hide with faster reverse animation
      Animated.parallel([
        Animated.timing(overlayOpacity, {
          toValue: 0,
          duration: 120,
          useNativeDriver: true,
        }),
        Animated.timing(containerOpacity, {
          toValue: 0,
          duration: 120,
          useNativeDriver: true,
        }),
        Animated.timing(containerScale, {
          toValue: 0.8,
          duration: 120,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [visible]);

  if (!visible) return null;

  const containerAnimatedStyle = {
    opacity: containerOpacity,
    transform: [{ scale: containerScale }],
  };

  return (
    <Animated.View 
      style={[
        styles.contextMenuContainer,
        containerAnimatedStyle,
        {
          alignItems: isUserMessage ? 'flex-end' : 'flex-start'
        }
      ]}
    >
      {/* Background overlay to close context menu */}
      <TouchableOpacity 
        style={styles.overlay}
        onPress={onClose}
        activeOpacity={1}
      />

      {/* Add to Diary Button - Only show for Bill messages */}
      {!isUserMessage && (
        <TouchableOpacity 
          style={styles.menuCard}
          onPress={onBookmark}
          activeOpacity={0.7}
        >
          <View style={styles.iconContainer}>
            <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <Path 
                d="M2.91602 17.2131C2.91602 17.2131 3.32573 7.21272 14.2278 3.19526C23.5673 0.706104 12.2238 10.3884 12.2238 10.3884L7.55404 10.847L9.72093 12.5622C7.59433 14.057 5.4686 14.7959 3.34202 14.7762" 
                stroke="#151515" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
              />
              <Path 
                d="M9.34766 17.2139H14.6894" 
                stroke="#151515" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
              />
            </Svg>
          </View>
          <Text style={styles.menuCardText}>
            {isBookmarked ? "Remover do diário" : "Adicionar ao diário"}
          </Text>
        </TouchableOpacity>
      )}

      {/* Share Button */}
      <TouchableOpacity 
        style={[styles.menuCard, styles.shareCard]}
        onPress={onShare}
        activeOpacity={0.7}
      >
        <View style={styles.iconContainer}>
          <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <Path 
              d="M9.11466 6.41699L6.24219 9.28947" 
              stroke="#151515" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round" 
            />
            <Path 
              fillRule="evenodd" 
              clipRule="evenodd" 
              d="M6.24332 9.28967L0.564154 5.81717C-0.180013 5.36217 -0.0291794 4.23967 0.809154 3.99717L14.2175 0.123838C14.98 -0.096995 15.685 0.614672 15.4567 1.37467L11.4783 14.678C11.2292 15.5113 10.1117 15.6547 9.65999 14.9113L6.24332 9.28967Z" 
              stroke="#151515" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round" 
            />
          </Svg>
        </View>
        <Text style={styles.menuCardText}>Compartilhar</Text>
      </TouchableOpacity>
    </Animated.View>
  );
});

export default AnimatedMessageContextMenu;

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: -1000,
    left: -1000,
    right: -1000,
    bottom: -1000,
    backgroundColor: 'transparent',
    zIndex: -1,
  },
  contextMenuContainer: {
    flexDirection: 'column',
    gap: 8,
    zIndex: 10000,
    elevation: 10000,
  },
  menuCard: {
    borderRadius: 12,
    alignItems: 'center',
    gap: 8,
    height: 52,
    paddingHorizontal: 16,
    paddingVertical: 16,
    flexDirection: 'row',
    backgroundColor: '#F9F8F3',
    borderWidth: 1,
    borderColor: '#DAD8CF',
    shadowColor: '#000',
    shadowOffset: {
      width: 4,
      height: 4,
    },
    shadowOpacity: 0.06,
    shadowRadius: 4,
    elevation: 4,
    minWidth: 185,
  },
  shareCard: {
    minWidth: 148,
  },
  iconContainer: {
    flexShrink: 0,
    width: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuCardText: {
    color: '#151515',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: 19.6, // 140% of 14px
  },
});
